/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useDebounce } from "usehooks-ts";
import BoldSubstring from "./BoldSubstring";
import { Combobox, Input } from "@src/common/components";
import { useGoogleAutocompleteApi } from "@src/common/lib/external/googlemaps/useAutocompleteApi";
import { useGooglePlaceDetails } from "@src/common/lib/external/googlemaps/useGooglePlaceDetails";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { AuthContext } from "@src/auth/components/AuthProvider";
import {
  AddressBookEntry,
  Maybe,
  useSearchFromAddressBookLazyQuery,
} from "@api/graphql/generated/generated-types";
import {
  buildAddressPredictions,
  Prediction,
} from "./buildAddressPredictions";
import { formatPhoneNumber } from "@src/common/lib/textUtils";
import classNames from "classnames";
import {
  Theme,
  useTheme,
} from "@src/common/components/ThemeProvider/ThemeProvider";

type AddressInputProps = {
  handleAddressChange: (
    address: google.maps.places.AutocompletePrediction | undefined
  ) => void;
  placeholder?: string;
  autoComplete?: string;
  control?: any;
  required?: boolean;
  value?: string;
  positive?: boolean;
  addressTypesForPlacesAutocomplete?: string[];
  size?: "mini" | "default" | "compact" | "large" | undefined;
  cleanOnBlur?: boolean;
  name?: string;
  disabled?: boolean;
  endCustomerId?: string;
  shouldReverseAddressSearch?: boolean;
  handleAddressBookEntrySelected?: (entry: AddressBookEntry) => void;
};

export default function AddressInput({
  handleAddressChange,
  placeholder = "Search for an address",
  autoComplete = "off",
  control,
  required = false,
  value,
  positive = false,
  addressTypesForPlacesAutocomplete,
  size,
  cleanOnBlur = true,
  name,
  disabled = false,
  endCustomerId,
  shouldReverseAddressSearch = false,
  handleAddressBookEntrySelected,
}: AddressInputProps) {
  const { courier } = useContext(AuthContext);
  const { effectiveTheme } = useTheme();
  const isDark = effectiveTheme === Theme.DARK;
  const [inputValue, setInputValue] = React.useState("");
  const debouncedQueryAddressValue = useDebounce<string>(inputValue, 500);
  const inputRef = useRef<HTMLElement>(null);
  const { place, fetchPlaceDetails } = useGooglePlaceDetails();
  const [isEditable, setIsEditable] = useState(true);

  useEffect(() => {
    if (place) {
      setInputValue(place.formatted_address || "");
      setIsEditable(false);
    }
  }, [place]);

  const {
    predictions: googlePredictions,
  }: {
    predictions: google.maps.places.AutocompletePrediction[];
  } = useGoogleAutocompleteApi(
    debouncedQueryAddressValue,
    addressTypesForPlacesAutocomplete,
    courier?.courier?.primaryLocation?.lngLat?.lat != null &&
      courier?.courier?.primaryLocation?.lngLat?.lng != null
      ? {
          center: {
            lat: courier.courier.primaryLocation.lngLat.lat,
            lng: courier.courier.primaryLocation.lngLat.lng,
          },
          radius: 50000,
        }
      : undefined
  );

  const [
    searchFromAddressBook,
    { data: addressBookEntriesData, loading: addressBookEntriesLoading },
  ] = useSearchFromAddressBookLazyQuery();

  useEffect(() => {
    if (shouldReverseAddressSearch) {
      searchFromAddressBook({
        variables: {
          courierId: courier?.courier?.id || "",
          endCustomerId: endCustomerId,
          googlePlaceIds: googlePredictions
            .filter((prediction) => prediction.place_id)
            .map((prediction) => prediction.place_id),
        },
      });
    }
  }, [
    googlePredictions,
    shouldReverseAddressSearch,
    courier?.courier?.id,
    endCustomerId,
    searchFromAddressBook,
  ]);

  const predictions: Prediction[] = useMemo(() => {
    if (shouldReverseAddressSearch && addressBookEntriesLoading) {
      return [];
    }
    return buildAddressPredictions({
      googlePredictions,
      addressBookEntries: addressBookEntriesData?.searchFromAddressBook ?? [],
      shouldReverseAddressSearch,
    });
  }, [
    addressBookEntriesData,
    googlePredictions,
    addressBookEntriesLoading,
    shouldReverseAddressSearch,
  ]);

  useEffect(() => {
    if (!value) {
      setInputValue("");
      setIsEditable(true);
    } else {
      if (value !== inputValue) {
        setInputValue(value);
        setIsEditable(false);
      }
    }
  }, [value]);

  function mapOptionToString(option: Prediction): string {
    return option.googleAutocompletePrediction.description;
  }

  function handleChange(nextValue: string, option?: Prediction | null) {
    setInputValue(nextValue);
    if (!option) {
      handleAddressChange(undefined);
      return;
    }

    if (option.addressBookEntry) {
      handleAddressBookEntrySelected?.(option.addressBookEntry);
      return;
    }

    handleAddressChange(option.googleAutocompletePrediction);
    if (option.googleAutocompletePrediction) {
      fetchPlaceDetails(option.googleAutocompletePrediction.place_id);
    }
  }

  const renderOptionNode = ({
    option,
  }: {
    option?: Prediction | unknown | undefined;
  }) => {
    const prediction: Prediction = option as Prediction;
    if (!prediction) {
      return null;
    }

    const addressBookEntry = prediction.addressBookEntry;
    const googlePrediction = prediction.googleAutocompletePrediction;

    var customerInfo = "";
    if (addressBookEntry) {
      const addToCustomerInfo = (text?: Maybe<string>) => {
        if (!text) {
          return;
        }
        if (customerInfo) {
          customerInfo += ", ";
        }
        customerInfo = `${customerInfo}${text}`;
      };
      addToCustomerInfo(addressBookEntry.companyName);
      addToCustomerInfo(addressBookEntry.contactName);
      addToCustomerInfo(
        addressBookEntry.phone && formatPhoneNumber(addressBookEntry.phone)
      );
      addToCustomerInfo(
        addressBookEntry.suite &&
          (/^\d+$/.test(addressBookEntry.suite)
            ? `STE ${addressBookEntry.suite}`
            : addressBookEntry.suite)
      );
      if (!customerInfo) {
        customerInfo = "Customer";
      }
    }

    return (
      <h1
        className="flex flex-col"
        key={`${googlePrediction.place_id}:${addressBookEntry?.id ?? "plain"}`}
      >
        {customerInfo && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {customerInfo}
          </p>
        )}
        <p className="-mt-1">
          <BoldSubstring
            text={googlePrediction.description}
            boldSubstrings={googlePrediction.matched_substrings}
          />
        </p>
      </h1>
    );
  };

  function handleClear() {
    setInputValue("");
    setIsEditable(true);
    handleAddressChange(undefined);
  }

  if (!control) {
    return (
      <Combobox
        size={size || undefined}
        inputRef={inputRef}
        value={inputValue || value || ""}
        positive={value ? true : positive}
        onBlur={() => {
          if (cleanOnBlur) {
            if (!value) {
              setInputValue("");
              setIsEditable(true);
            }
          }
        }}
        onChange={(b, a) => {
          handleChange(b, a as Prediction);
        }}
        mapOptionToString={(a) => mapOptionToString(a as Prediction)}
        options={predictions}
        name="async-options"
        mapOptionToNode={renderOptionNode}
        overrides={{
          Input: {
            props: {
              placeholder: placeholder,
              autoComplete: autoComplete,
            },
          },
          ListItem: {
            style: {
              paddingTop: "18px",
              paddingBottom: "18px",
            },
          },
        }}
        disabled={disabled}
      />
    );
  }

  return (
    <Controller
      name={name || "streetAddress"}
      control={control}
      rules={{ required: value ? false : required }}
      render={({ fieldState: { error }, field: { ref: callbackRef } }) => {
        callbackRef(inputRef.current);
        return isEditable && !disabled ? (
          <Combobox
            error={error ? true : false}
            size={size || undefined}
            inputRef={inputRef}
            value={inputValue || value || ""}
            positive={value ? true : positive}
            onBlur={() => {
              if (cleanOnBlur) {
                if (!value) {
                  setInputValue("");
                  setIsEditable(true);
                }
              }
            }}
            onChange={(b, a) => {
              handleChange(b, a as Prediction);
            }}
            mapOptionToString={(a) => mapOptionToString(a as Prediction)}
            options={predictions}
            name="async-options"
            mapOptionToNode={renderOptionNode}
            overrides={{
              Input: {
                props: {
                  placeholder: placeholder,
                  error: error ? true : false,
                  autoComplete: autoComplete,
                },
              },
              ListItem: {
                style: {
                  paddingTop: "18px",
                  paddingBottom: "18px",
                },
              },
            }}
          />
        ) : (
          <Input
            disabled={disabled}
            size={size || undefined}
            value={inputValue || value || ""}
            readOnly={true}
            clearable={true}
            positive={value ? true : positive}
            overrides={{
              Root: {
                style: {
                  position: "relative",
                },
              },
              Input: {
                style: {
                  backgroundColor: isDark
                    ? "rgb(11 21 35)"
                    : "rgb(249 250 251)",
                  color: isDark ? "rgb(243 244 246)" : undefined,
                  marginRight: "1rem",
                  cursor: "auto",
                  colorScheme: isDark ? "dark" : "light",
                },
              },
              ClearIconContainer: {
                style: {
                  border: "1px solid green",
                  display: "none",
                },
              },
              After: () => (
                <XMarkIcon
                  className={classNames({
                    "w-6 h-6 ": true,
                    "my-auto cursor-pointer": !disabled,
                  })}
                  onClick={() => !disabled && handleClear()}
                  style={{
                    position: "absolute",
                    right: "0px",
                    top: size === "mini" ? "2px" : "10px",
                  }}
                />
              ),
            }}
          />
        );
      }}
    />
  );
}
